import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import Badge from '../components/Badge';
import SLAIndicator from '../components/SLAIndicator';
import Button from '../components/Button';
import ApprovalStepper from '../components/ticket/ApprovalStepper';
import AttachmentsPanel from '../components/ticket/AttachmentsPanel';
import CollabPanel from '../components/ticket/CollabPanel';
import CannedResponsePicker from '../components/ticket/CannedResponsePicker';
import KbSuggestions from '../components/ticket/KbSuggestions';
import TicketExtrasPanel from '../components/ticket/TicketExtrasPanel';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/Toast';
import { useWorkStatuses, workStatusColor } from '../hooks/useWorkStatuses';
import { fmtDT, apiErrorMessage, requesterStatusLabel } from '../utils/helpers';
import { hasRole } from '../utils/roleUtils';
import type { Ticket, TimelineEntry } from '../types';

const CLOSURE_CATEGORIES = ['Resolved', 'Workaround', 'No Action Needed', 'Escalated'] as const;

const CANNOT_IMPLEMENT_REASONS = [
  'Technically not feasible', 'Out of scope for IT services',
  'Budget constraints', 'Security policy restriction',
  'Duplicate request', 'Other',
];

// Statuses where the ticket is finished — status checks / replies don't apply.
const TERMINAL_STATUSES = [
  'Closed', 'ClosedCompleted', 'ClosedCannotImplement', 'ClosedOutOfScope', 'ClosedDuplicate', 'Rejected',
];

interface ITStaff { id: string; name: string; email: string; isAvailable?: boolean; }
interface LineManager { id: string; name: string; email: string; department: string | null; }
interface TopMgmtApprover {
  stepId: string; stepOrder: number; name: string;
  approverUserId: string | null; approverName: string | null; approverActive: boolean;
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}


export default function TicketDetail() {
  const { id }       = useParams<{ id: string }>();
  const { user }     = useAuth();
  const navigate     = useNavigate();
  const location     = useLocation();
  const { toast }    = useToast();
  const [ticket,  setTicket]  = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [action,  setAction]  = useState<'approve' | 'reject' | 'transfer' | 'resolve' | 'redirectLm' | null>(null);
  const [working, setWorking] = useState(false);

  // SR Closed modal (legacy)
  const [showCloseModal,    setShowCloseModal]    = useState(false);
  const [resolutionNotes,   setResolutionNotes]   = useState('');
  const [timeSpent,         setTimeSpent]         = useState('');
  const [closureCategory,   setClosureCategory]   = useState<typeof CLOSURE_CATEGORIES[number] | ''>('');
  const [closingTicket,     setClosingTicket]     = useState(false);

  // Assign panel
  const [itStaff,       setITStaff]       = useState<ITStaff[]>([]);
  const [assignTarget,  setAssignTarget]  = useState('self');
  const [assignReason,  setAssignReason]  = useState('');
  const [assignSla,     setAssignSla]     = useState('');
  const [assigning,     setAssigning]     = useState(false);

  // Transfer-to-Top-Management picker (only populated if an escalation chain is
  // configured) — one or more named approvers may be required; only the ones
  // checked here become a real gate, everyone else in the chain is skipped.
  const [topMgmtApprovers,   setTopMgmtApprovers]   = useState<TopMgmtApprover[]>([]);
  const [selectedStepOrders, setSelectedStepOrders] = useState<number[]>([]);

  // Redirect-Line-Manager-approval picker — the requester's actual LM may be
  // unavailable/unresponsive; IT Manager/admin can point the LM step at someone else.
  const [lineManagers,      setLineManagers]      = useState<LineManager[]>([]);
  const [redirectTarget,    setRedirectTarget]     = useState('');

  // IT Manager triage on an Approved, unassigned ticket
  const [clarifyModal,   setClarifyModal]   = useState(false);
  const [clarifyNote,    setClarifyNote]    = useState('');
  const [clarifying,     setClarifying]     = useState(false);

  // Requester's reply to a clarification request
  const [resubmitNote, setResubmitNote] = useState('');
  const [resubmitting, setResubmitting] = useState(false);
  const [deferModal,     setDeferModal]     = useState(false);
  const [deferReason,    setDeferReason]    = useState('');
  const [deferUntil,     setDeferUntil]     = useState('');
  const [deferring,      setDeferring]      = useState(false);
  const [cannotModal,    setCannotModal]    = useState(false);
  const [cannotCategory, setCannotCategory] = useState('');
  const [cannotExplain,  setCannotExplain]  = useState('');
  const [cannotWorking,  setCannotWorking]  = useState(false);

  // Work status (admin-managed informational sub-status)
  const { statuses: workStatuses } = useWorkStatuses();
  const [settingWs, setSettingWs] = useState(false);

  // Status check (requester asks) / status reply (assignee / IT manager answers)
  const [showAskStatus, setShowAskStatus] = useState(false);
  const [statusQuery,   setStatusQuery]   = useState('');
  const [askingStatus,  setAskingStatus]  = useState(false);
  const [statusReply,   setStatusReply]   = useState('');
  const [sendingReply,  setSendingReply]  = useState(false);

  // `silent` skips the loading spinner and doesn't null the ticket out on a transient
  // error — used by the background poll below so another user's action (IT staff
  // resolving, requester confirming, etc.) shows up here without a manual refresh.
  const load = async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    try {
      const r = await api.get<Ticket>(`/tickets/${id}`);
      setTicket(r.data);
    } catch {
      if (!opts?.silent) setTicket(null);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(() => load({ silent: true }), 15000);
    return () => clearInterval(interval);
  }, [id]); // eslint-disable-line

  // Pre-fill the SLA field with the priority-based suggestion once, so the IT
  // Manager sees a sensible starting point rather than a blank required field —
  // only matters before the first assignment (see ticketService.assignTicket).
  useEffect(() => {
    if (ticket && !ticket.assignedToId && ticket.slaHours) setAssignSla(String(ticket.slaHours));
  }, [ticket?.id]); // eslint-disable-line

  const loadITStaff = useCallback(async () => {
    try {
      const r = await api.get<ITStaff[]>('/admin/users/it-staff-list');
      setITStaff(r.data);
    } catch {}
  }, []);

  useEffect(() => {
    if (hasRole(user, 'it_staff') || hasRole(user, 'it_manager') || hasRole(user, 'admin')) {
      loadITStaff();
    }
  }, [loadITStaff, user]); // eslint-disable-line

  useEffect(() => {
    if (!id || !(hasRole(user, 'line_manager') || hasRole(user, 'it_manager') || hasRole(user, 'admin'))) return;
    api.get<{ steps: TopMgmtApprover[] }>(`/tickets/${id}/top-mgmt-approvers`)
      .then(r => {
        const steps = r.data.steps ?? [];
        setTopMgmtApprovers(steps);
        const firstUsable = steps.find(s => s.approverActive && s.approverUserId);
        setSelectedStepOrders(firstUsable ? [firstUsable.stepOrder] : []);
      })
      .catch(() => {});
  }, [id, user]); // eslint-disable-line

  useEffect(() => {
    if (!(hasRole(user, 'it_manager') || hasRole(user, 'admin'))) return;
    api.get<LineManager[]>('/admin/users/line-managers').then(r => setLineManagers(r.data)).catch(() => {});
  }, [user]); // eslint-disable-line

  const approve = async () => {
    setWorking(true);
    try {
      await api.put(`/tickets/${id}/approve`, { comment });
      toast('Ticket approved', 'success');
      await load();
      setAction(null); setComment('');
    } catch (e: any) {
      toast(apiErrorMessage(e, 'Failed to approve'), 'error');
    } finally { setWorking(false); }
  };

  const reject = async () => {
    if (!comment.trim()) { toast('Rejection reason is required', 'error'); return; }
    setWorking(true);
    try {
      await api.put(`/tickets/${id}/reject`, { comment });
      toast('Ticket rejected', 'success');
      await load();
      setAction(null); setComment('');
    } catch (e: any) {
      toast(apiErrorMessage(e, 'Failed to reject'), 'error');
    } finally { setWorking(false); }
  };

  const transfer = async () => {
    setWorking(true);
    try {
      await api.put(`/tickets/${id}/transfer-top-mgmt`, {
        comment,
        ...(topMgmtApprovers.length && selectedStepOrders.length ? { stepOrders: selectedStepOrders } : {}),
      });
      toast('Transferred to Top Management', 'success');
      await load();
      setAction(null); setComment('');
    } catch (e: any) {
      toast(apiErrorMessage(e, 'Failed to transfer'), 'error');
    } finally { setWorking(false); }
  };

  const redirectLm = async () => {
    if (!redirectTarget) { toast('Select a Line Manager to redirect to', 'error'); return; }
    setWorking(true);
    try {
      await api.put(`/tickets/${id}/redirect-lm`, { newLineManagerId: redirectTarget, note: comment });
      toast('Line Manager approval redirected', 'success');
      await load();
      setAction(null); setComment(''); setRedirectTarget('');
    } catch (e: any) {
      toast(apiErrorMessage(e, 'Failed to redirect'), 'error');
    } finally { setWorking(false); }
  };

  const startWork = async () => {
    setWorking(true);
    try {
      await api.put(`/tickets/${id}/start`);
      toast('Work started', 'success');
      await load();
    } catch (e: any) {
      toast(apiErrorMessage(e, 'Failed to start work'), 'error');
    } finally { setWorking(false); }
  };

  const resolve = async () => {
    setWorking(true);
    try {
      await api.put(`/tickets/${id}/resolve`, { comment });
      toast('Ticket marked as resolved — requester will be notified', 'success');
      await load();
      setComment('');
    } catch (e: any) {
      toast(apiErrorMessage(e, 'Failed to resolve'), 'error');
    } finally { setWorking(false); }
  };

  const confirmResolved = async () => {
    setWorking(true);
    try {
      await api.put(`/tickets/${id}/confirm-resolved`);
      toast('Resolution confirmed — the IT team will finalize closure', 'success');
      await load();
    } catch (e: any) {
      toast(apiErrorMessage(e, 'Failed to confirm'), 'error');
    } finally { setWorking(false); }
  };

  const doAssign = async () => {
    const isSelf = assignTarget === 'self';
    if (!isSelf && !assignReason.trim()) {
      toast('Please enter a reason for assigning to another IT staff member', 'error');
      return;
    }
    const isManagerOrAdmin = hasRole(user, 'it_manager') || hasRole(user, 'admin');
    if (isManagerOrAdmin && !ticket?.assignedToId && !assignSla.trim()) {
      toast('Please state the SLA (in hours) for this ticket', 'error');
      return;
    }
    setAssigning(true);
    try {
      const assignedToId = isSelf ? user!.id : assignTarget;
      await api.put(`/tickets/${id}/assign-to`, {
        assignedToId,
        reason: isSelf ? undefined : assignReason.trim(),
        slaHours: assignSla.trim() ? Number(assignSla) : undefined,
      });
      toast('Ticket assigned successfully', 'success');
      setAssignReason('');
      await load();
    } catch (e: any) {
      toast(apiErrorMessage(e, 'Failed to assign ticket'), 'error');
    } finally { setAssigning(false); }
  };

  const doClarify = async () => {
    if (!clarifyNote.trim()) { toast('Please enter the clarification needed', 'error'); return; }
    setClarifying(true);
    try {
      await api.put(`/tickets/${id}/request-clarification`, { note: clarifyNote });
      toast('Returned for clarification', 'success');
      setClarifyModal(false); setClarifyNote('');
      await load();
    } catch (e: any) {
      toast(apiErrorMessage(e, 'Failed to request clarification'), 'error');
    } finally { setClarifying(false); }
  };

  const doResubmit = async () => {
    if (!resubmitNote.trim()) { toast('Please describe what changed before resubmitting', 'error'); return; }
    setResubmitting(true);
    try {
      await api.put(`/tickets/${id}/resubmit`, { note: resubmitNote });
      toast('Request resubmitted', 'success');
      setResubmitNote('');
      await load();
    } catch (e: any) {
      toast(apiErrorMessage(e, 'Failed to resubmit'), 'error');
    } finally { setResubmitting(false); }
  };

  const doDefer = async () => {
    if (!deferReason.trim() || !deferUntil) { toast('Please fill all deferral fields', 'error'); return; }
    setDeferring(true);
    try {
      await api.put(`/tickets/${id}/defer`, { reason: deferReason, deferUntil });
      toast('Ticket deferred', 'success');
      setDeferModal(false); setDeferReason(''); setDeferUntil('');
      await load();
    } catch (e: any) {
      toast(apiErrorMessage(e, 'Failed to defer'), 'error');
    } finally { setDeferring(false); }
  };

  const doCannot = async () => {
    if (!cannotCategory || !cannotExplain.trim()) {
      toast('Please select a reason and provide an explanation', 'error'); return;
    }
    setCannotWorking(true);
    try {
      await api.put(`/tickets/${id}/cannot-implement`, { reasonCategory: cannotCategory, explanation: cannotExplain });
      toast('Ticket closed — cannot implement', 'success');
      setCannotModal(false); setCannotCategory(''); setCannotExplain('');
      await load();
    } catch (e: any) {
      toast(apiErrorMessage(e, 'Failed to close ticket'), 'error');
    } finally { setCannotWorking(false); }
  };

  const doSetWorkStatus = async (value: string) => {
    setSettingWs(true);
    try {
      await api.put(`/tickets/${id}/work-status`, { workStatus: value });
      toast(value ? 'Work status updated' : 'Work status cleared', 'success');
      await load();
    } catch (e: any) {
      toast(apiErrorMessage(e, 'Failed to update work status'), 'error');
    } finally { setSettingWs(false); }
  };

  const askStatus = async () => {
    if (!statusQuery.trim()) { toast('Please enter your question', 'error'); return; }
    setAskingStatus(true);
    try {
      await api.put(`/tickets/${id}/status-check`, { query: statusQuery.trim() });
      toast('Status check sent — the IT team will reply on this ticket', 'success');
      setStatusQuery(''); setShowAskStatus(false);
      await load();
    } catch (e: any) {
      toast(apiErrorMessage(e, 'Failed to send status check'), 'error');
    } finally { setAskingStatus(false); }
  };

  const sendStatusReply = async () => {
    if (!statusReply.trim()) { toast('Please enter a status update', 'error'); return; }
    setSendingReply(true);
    try {
      await api.put(`/tickets/${id}/status-reply`, { message: statusReply.trim() });
      toast('Status update sent to the requester', 'success');
      setStatusReply('');
      await load();
    } catch (e: any) {
      toast(apiErrorMessage(e, 'Failed to send status update'), 'error');
    } finally { setSendingReply(false); }
  };

  const closeSR = async () => {
    if (!resolutionNotes.trim()) { toast('Resolution notes are required', 'error'); return; }
    if (!closureCategory)        { toast('Closure category is required', 'error'); return; }
    setClosingTicket(true);
    try {
      await api.put(`/tickets/${id}/close`, {
        resolutionNotes,
        timeSpent:        timeSpent ? parseFloat(timeSpent) : null,
        closureCategory,
      });
      toast('SR closed successfully', 'success');
      setShowCloseModal(false);
      await load();
    } catch (e: any) {
      toast(apiErrorMessage(e, 'Failed to close SR'), 'error');
      await load(); // the ticket may have moved on (e.g. requester already confirmed) — refresh the stale state
    } finally { setClosingTicket(false); }
  };

  // The sidebar highlight should reflect the ticket's CURRENT status live, not just
  // wherever the viewer happened to navigate from. Approving/assigning/confirming/SR
  // Closed all move status forward in place (no re-navigation) — without this, the
  // sidebar would keep showing the tab you originally arrived from even after the
  // ticket has moved into a different tab's territory (e.g. still "Approvals" after
  // you just approved it, or still "Workspace" after SR Closed finished it).
  useEffect(() => {
    if (!ticket || !user) return;
    const isReq = ticket.requestedBy === user.id;
    const nav = isReq ? '/tickets'
      : ['PendingITManager', 'PendingLineManager', 'PendingTopManagement'].includes(ticket.status) ? '/approvals'
      : TERMINAL_STATUSES.includes(ticket.status) ? '/all-tickets'
      : '/workspace';
    if ((location.state as any)?.activeNav !== nav) {
      navigate(location.pathname, { replace: true, state: { activeNav: nav } });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.status, ticket?.requestedBy, user?.id]);

  if (loading) return (
    <div className="p-6 flex items-center gap-2 text-sm text-gray-400">
      <div className="w-4 h-4 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
      Loading…
    </div>
  );
  if (!ticket) return <div className="p-6 text-sm text-red-600">Ticket not found</div>;

  const status      = ticket.status;
  const isITManager = hasRole(user, 'it_manager') || hasRole(user, 'admin');
  const isITTeam    = hasRole(user, 'it_staff') || isITManager;
  const isAssignee  = user && ticket.assignedToId === user.id;
  const isRequester = user && ticket.requestedBy === user.id;
  const isOpenTicket = !TERMINAL_STATUSES.includes(status);

  // Status check: requester asks a question; it lands with the assignee, else the IT manager.
  const canAskStatus   = isRequester && isOpenTicket;
  const canReplyStatus = isITTeam && (isAssignee || isITManager) && isOpenTicket;

  // A requester can never approve/reject their own request, regardless of role.
  // IT Manager reviews first (security/compliance), then Line Manager (necessity/
  // business justification) — Top Management only if the Line Manager escalates.
  const canApproveITFirst = isITManager && !isRequester && status === 'PendingITManager';
  // Line Manager is always a specific person (the requester's real manager) — the
  // role check alone isn't enough, since a multi-role account (e.g. admin who also
  // holds line_manager/it_manager) would otherwise see this for every ticket, not
  // just the ones actually routed to them.
  const isTicketsLineManager = !ticket.currentPendingWithId || ticket.currentPendingWithId === user?.id;
  const canApproveLM  = hasRole(user, 'line_manager')   && !isRequester && status === 'PendingLineManager' && isTicketsLineManager;
  const canApproveTop = hasRole(user, 'top_management') && !isRequester && status === 'PendingTopManagement'
    && (!ticket.currentPendingWithId || ticket.currentPendingWithId === user?.id);
  const canApprove    = canApproveITFirst || canApproveLM || canApproveTop;
  // Escalating to Top Management ("another round of approval") is available
  // alongside Approve/Reject at either stage — the Line Manager's own decision at
  // PendingLineManager, or IT Manager's own decision at the earlier PendingITManager
  // stage (broadcast there, same as canApproveITFirst — no specific-person check).
  const canTransfer   = (hasRole(user, 'line_manager') && !isRequester && status === 'PendingLineManager' && isTicketsLineManager)
    || (isITManager && !isRequester && status === 'PendingITManager');
  // The requester's actual Line Manager may be unavailable/unresponsive — IT
  // Manager/admin can redirect that approval to someone else, either pre-emptively
  // (still at PendingITManager, before it's even reached the LM) or once it's
  // already sitting with the LM (PendingLineManager).
  const canRedirectLM = isITManager && !isRequester
    && (status === 'PendingITManager' || status === 'PendingLineManager');

  // IT Manager triage on an already-Approved, unassigned ticket — Defer / Cannot
  // Implement live here (Request Clarification is also available earlier, at the
  // PendingITManager review stage — see the Actions block). A multi-item ticket
  // never actually reaches this point with 2+ items — each item
  // beyond the first auto-splits into its own ticket the moment approval finalizes
  // (see splitApprovedTicketItems on the backend), so there's nothing left to
  // manually split here.
  const canTriageApproved = isITManager && !isRequester && status === 'Approved' && !ticket.assignedToId;

  // Assign panel: only once the ticket has actually cleared approval (Approved/
  // Deferred/Assigned) — approving and assigning are separate, sequential steps in
  // separate tabs (Approvals, then Workspace's unassigned queue).
  // Two more gates on top of that:
  // (1) once assigned to someone other than the viewer, it disappears for everyone
  //     else — it's that person's ticket to manage now.
  // (2) once Start Work has been clicked (InProgress), reassignment is locked out
  //     entirely, even for the assignee — work is underway, no more casual reassigning.
  const notAssignedToSomeoneElse = !ticket.assignedToId || ticket.assignedToId === user?.id;
  const canAssign   = notAssignedToSomeoneElse && isITTeam && !isRequester && ['Approved', 'Deferred', 'Assigned'].includes(status);

  // Work status: assignee or IT manager, while the ticket is being worked
  const canSetWorkStatus = isITTeam && (isAssignee || isITManager)
    && ['Approved', 'Assigned', 'InProgress'].includes(status);

  // IT Staff actions — a strict two-step close sequence: Mark Resolved first
  // (InProgress -> Resolved), then SR Closed is the ONLY action that finalizes closure
  // (Resolved -> Closed). Neither step can be skipped or triggered out of order.
  // All three (Start Work / Mark Resolved / SR Closed) are strictly assignee-only —
  // an IT Manager who assigned the ticket to someone else doesn't retain these actions
  // just by virtue of their role once it's someone else's ticket to work.
  const canStartWork        = isITTeam && isAssignee && status === 'Assigned';
  const canResolve          = isITTeam && isAssignee && status === 'InProgress';
  // Confirming doesn't close the ticket by itself — SR Closed (by the assignee) is the
  // sole final closure action; confirming just unblocks it immediately (see below).
  const canConfirmResolved  = user && ticket.requestedBy === user.id && status === 'Resolved' && !ticket.requesterConfirmedAt;
  const canCloseSR          = isAssignee && status === 'Resolved';

  // SR Closed only becomes usable 5 days after Mark Resolved — giving the requester a
  // window to confirm on their own first — UNLESS they already confirmed, which unblocks
  // it immediately. Mirrors the backend's autoCloseResolved window.
  const RESOLVED_CONFIRMATION_WINDOW_MS = 5 * 24 * 60 * 60 * 1000;
  const resolvedAt      = ticket.resolvedAt ? new Date(ticket.resolvedAt).getTime() : null;
  const srCloseEligible = !!ticket.requesterConfirmedAt
    || (resolvedAt != null && (Date.now() - resolvedAt) >= RESOLVED_CONFIRMATION_WINDOW_MS);
  const srCloseAvailableAt = resolvedAt != null ? new Date(resolvedAt + RESOLVED_CONFIRMATION_WINDOW_MS) : null;

  const requesterName   = ticket.requester?.name    || '—';
  const lineManagerName = ticket.lineManager?.name  || '—';
  const itManagerName   = ticket.itManager?.name    || '—';
  const assignedToName  = ticket.assignedTo?.name   || null;

  // For a recurring issue — raise a brand-new request pre-filled with this
  // (finished) one's details, instead of retyping everything from scratch.
  // Only makes sense once the old ticket is actually done; while it's still
  // open there's nothing to "raise again" yet.
  const canRaiseAgain = isRequester && TERMINAL_STATUSES.includes(status);
  const raiseAgain = () => {
    navigate('/tickets/new', {
      state: {
        prefill: {
          fromTicketId:  ticket.id,
          purpose:       ticket.purpose ?? '',
          priority:      ticket.priority,
          requestFor:    ticket.requestFor ?? 'myself',
          contactNumber: ticket.contactNumber ?? '',
          beneficiaryName:     ticket.beneficiaryName     ?? '',
          beneficiaryUsername: ticket.beneficiaryUsername ?? '',
          beneficiaryDept:     ticket.beneficiaryDept      ?? '',
          beneficiaryTitle:    ticket.beneficiaryTitle     ?? '',
          beneficiaryEmail:    ticket.beneficiaryEmail     ?? '',
          // id/ticketId/createdAt belong to the old item, not a new submission.
          // startDate/endDate are dropped too — they're specific to the old
          // request's timing, so the user picks fresh ones rather than
          // resubmitting a now-stale (likely past) date.
          items: (ticket.items ?? []).map(({ id, ticketId, createdAt, startDate, endDate, ...rest }: any) => rest),
        },
      },
    });
  };

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-gray-600 text-xl">←</button>
        <h1 className="text-base font-bold text-[#010E23] font-mono">{ticket.id}</h1>
        <Badge status={ticket.status} label={requesterStatusLabel(ticket, !!isRequester) ?? undefined} />
        {canRaiseAgain && (
          <Button size="sm" variant="secondary" className="ml-auto" onClick={raiseAgain}>
            Raise This Again
          </Button>
        )}
        {/* Work status: editable dropdown for IT team, read-only pill otherwise */}
        {ticket.workStatus && !canSetWorkStatus && (
          <span className="text-[11px] font-medium rounded-full px-2 py-0.5"
            style={{ background: `${workStatusColor(workStatuses, ticket.workStatus)}1A`, color: workStatusColor(workStatuses, ticket.workStatus) }}>
            {ticket.workStatus}
          </span>
        )}
        {canSetWorkStatus && (
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 rounded-full"
              style={{ background: workStatusColor(workStatuses, ticket.workStatus) }} />
            <select
              value={ticket.workStatus ?? ''}
              disabled={settingWs}
              onChange={e => doSetWorkStatus(e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1 outline-none focus:border-violet-500 bg-white"
              title="Work status"
            >
              <option value="">No work status</option>
              {workStatuses.map(ws => <option key={ws.id} value={ws.label}>{ws.label}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* Header info */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
        <Info label="Requester"    value={requesterName} />
        <Info label="Department"   value={ticket.department || '—'} />
        <Info label="Priority"     value={ticket.priority} />
        <Info label="Submitted"    value={fmtDT(ticket.submittedAt)} />
        <Info label="Line Manager" value={lineManagerName} />
        <Info label="IT Manager"   value={itManagerName} />
        {assignedToName && (
          <Info label="Assigned To"  value={assignedToName} />
        )}
        {ticket.requiredByDate && (
          <Info label="Required By"  value={fmtDT(ticket.requiredByDate)} />
        )}
        {ticket.contactNumber && (
          <Info label="Contact"      value={ticket.contactNumber} />
        )}
        <div className="col-span-2">
          <p className="text-xs text-gray-400 mb-1">SLA</p>
          <SLAIndicator ticket={ticket} />
        </div>
      </div>

      {/* Status check: requester asks; lands with the assignee or IT manager */}
      {canAskStatus && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-[#010E23]">Need an update on this request?</h2>
            {!showAskStatus && (
              <Button variant="secondary" size="sm" onClick={() => setShowAskStatus(true)}>
                Ask for Status Update
              </Button>
            )}
          </div>
          {showAskStatus && (
            <div className="space-y-2">
              <textarea
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-violet-600 resize-none"
                rows={2}
                placeholder="What would you like to know? e.g. 'Any update on this request?'"
                value={statusQuery}
                onChange={e => setStatusQuery(e.target.value)}
              />
              <div className="flex gap-2">
                <Button variant="primary" size="sm" disabled={askingStatus || !statusQuery.trim()} onClick={askStatus}>
                  {askingStatus ? 'Sending…' : 'Send'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setShowAskStatus(false); setStatusQuery(''); }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Status reply: assignee / IT manager sends a status update back to the requester */}
      {canReplyStatus && (
        <div className="bg-white rounded-xl border border-violet-100 shadow-sm p-5 space-y-2">
          <h2 className="text-sm font-semibold text-[#010E23]">Send a Status Update to the Requester</h2>
          <textarea
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-violet-600 resize-none"
            rows={2}
            placeholder="Let the requester know where things stand…"
            value={statusReply}
            onChange={e => setStatusReply(e.target.value)}
          />
          <Button variant="primary" size="sm" disabled={sendingReply || !statusReply.trim()} onClick={sendStatusReply}>
            {sendingReply ? 'Sending…' : 'Send Status Update'}
          </Button>
        </div>
      )}

      {/* Tags & watchers */}
      {ticket.id && <CollabPanel ticketId={ticket.id} initialTags={(ticket as any).tags ?? []} />}

      {/* CSAT / time tracking / merge */}
      {ticket.id && <TicketExtrasPanel ticketId={ticket.id} status={ticket.status} requesterId={(ticket as any).requestedBy} />}

      {/* Approval progress (configurable chain) — self-hides when the ticket has no chain */}
      {ticket.id && <ApprovalStepper ticketId={ticket.id} />}

      {/* Split from — this ticket is itself a split-off item from another request */}
      {ticket.parentTicketId && (
        <div className="bg-violet-50 rounded-xl border border-violet-100 p-4">
          <p className="text-sm text-violet-800">
            Split from{' '}
            <button
              className="font-mono underline hover:no-underline"
              onClick={() => navigate(`/tickets/${ticket.parentTicketId}`, { state: { activeNav: '/workspace' } })}
            >
              {ticket.parentTicketId}
            </button>
          </p>
        </div>
      )}

      {/* Split off — items from this ticket were handed to a different manager */}
      {!!ticket.childTickets?.length && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-2">
          <h2 className="text-sm font-semibold text-[#010E23]">Split Off</h2>
          {ticket.childTickets.map(c => (
            <div key={c.id} className="flex items-center justify-between gap-3 text-sm">
              <button
                className="font-mono text-xs text-violet-700 hover:underline"
                onClick={() => navigate(`/tickets/${c.id}`, { state: { activeNav: '/approvals' } })}
              >
                {c.id}
              </button>
              <span className="text-xs text-gray-500">{c.items?.[0]?.category ?? '—'}</span>
              <Badge status={c.status} small />
            </div>
          ))}
        </div>
      )}

      {/* Proof documents */}
      {ticket.id && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <AttachmentsPanel
            ticketId={ticket.id}
            currentUserId={user?.id}
            isAdminOrManager={isITManager}
          />
        </div>
      )}

      {/* Clarification note */}
      {status === 'PendingRequester' && ticket.clarificationNote && (
        <div className="bg-purple-50 rounded-xl border border-purple-100 p-5 space-y-3">
          <div className="space-y-1">
            <h2 className="text-sm font-semibold text-purple-800">Clarification Required</h2>
            <p className="text-sm text-purple-700">{ticket.clarificationNote}</p>
          </div>
          {isRequester ? (
            <div className="space-y-2 pt-2 border-t border-purple-200">
              <label className="text-xs font-medium text-purple-800">Your response</label>
              <textarea value={resubmitNote} onChange={e => setResubmitNote(e.target.value)} rows={3}
                placeholder="Describe the additional information or changes requested…"
                className="w-full text-sm rounded-lg border border-purple-200 p-2.5 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400" />
              <Button size="sm" onClick={doResubmit} disabled={resubmitting}>
                {resubmitting ? 'Resubmitting…' : 'Resubmit Request'}
              </Button>
            </div>
          ) : (
            <p className="text-xs text-purple-500">Waiting on the requester to update and resubmit this request.</p>
          )}
        </div>
      )}

      {/* Deferral info */}
      {status === 'Deferred' && (
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-5 space-y-1">
          <h2 className="text-sm font-semibold text-gray-700">Ticket Deferred</h2>
          {ticket.deferralReason && <p className="text-sm text-gray-600">Reason: {ticket.deferralReason}</p>}
          {ticket.deferredUntil  && <p className="text-xs text-blue-600">Review date: {new Date(ticket.deferredUntil).toLocaleDateString()}</p>}
        </div>
      )}

      {/* Resolved — awaiting confirmation (only until the requester actually confirms —
          afterwards the "Resolution Confirmed" banner below takes over) */}
      {isRequester && status === 'Resolved' && !ticket.requesterConfirmedAt && (
        <div className="bg-teal-50 rounded-xl border border-teal-100 p-5 space-y-1">
          <h2 className="text-sm font-semibold text-teal-800">Resolved — Awaiting Your Confirmation</h2>
          <p className="text-sm text-teal-700">The IT team has resolved this request. Please confirm if the issue has been fixed.</p>
        </div>
      )}

      {/* Closure info (shown when ticket is closed by various routes) */}
      {['Closed', 'ClosedCompleted', 'ClosedCannotImplement', 'ClosedOutOfScope', 'ClosedDuplicate'].includes(status) && (ticket.resolutionNotes || ticket.closureCategory) && (
        <div className="bg-white rounded-xl border border-green-100 shadow-sm p-5 space-y-3">
          <h2 className="text-sm font-semibold text-green-800">Closure Summary</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            {ticket.closureCategory && <Info label="Category"      value={ticket.closureCategory} />}
            {ticket.timeSpent != null && <Info label="Time Spent"   value={`${ticket.timeSpent}h`} />}
            {ticket.closedAt        && <Info label="Closed At"     value={fmtDT(ticket.closedAt)} />}
            {ticket.closedBy?.name  && <Info label="Closed By"     value={ticket.closedBy.name} />}
          </div>
          {ticket.resolutionNotes && (
            <div>
              <p className="text-xs text-gray-400 mb-1">Resolution Notes</p>
              <p className="text-sm text-gray-700 leading-relaxed">{ticket.resolutionNotes}</p>
            </div>
          )}
        </div>
      )}

      {/* Business purpose */}
      {ticket.purpose && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h2 className="text-sm font-semibold text-[#010E23] mb-2">Business Purpose</h2>
          <p className="text-xs text-gray-600 leading-relaxed">{ticket.purpose}</p>
        </div>
      )}

      {/* Beneficiary info */}
      {ticket.requestFor === 'someone else' && ticket.beneficiaryName && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h2 className="text-sm font-semibold text-[#010E23] mb-3">Beneficiary</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Info label="Name"       value={ticket.beneficiaryName} />
            {ticket.beneficiaryDept  && <Info label="Department" value={ticket.beneficiaryDept} />}
            {ticket.beneficiaryTitle && <Info label="Job Title"  value={ticket.beneficiaryTitle} />}
            {ticket.beneficiaryEmail && <Info label="Email"      value={ticket.beneficiaryEmail} />}
          </div>
        </div>
      )}

      {/* Service items */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <h2 className="text-sm font-semibold text-[#010E23] mb-3">Service Items</h2>
        <div className="space-y-3">
          {ticket.items?.map((item, i) => (
            <div key={i} className="bg-gray-50 rounded-lg p-3 text-xs">
              <p className="font-semibold text-[#26148C] mb-1.5">{i + 1}. {item.category}</p>
              <div className="grid grid-cols-2 gap-1.5">
                {Object.entries(item)
                  .filter(([k]) => !['id', 'ticketId', 'itemOrder', 'category'].includes(k))
                  .map(([k, v]) => v ? (
                    <span key={k} className="text-gray-600">
                      <b className="text-gray-500 font-medium capitalize mr-1">
                        {k.replace(/([A-Z])/g, ' $1').trim()}:
                      </b>
                      {String(v)}
                    </span>
                  ) : null)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Additional Information (custom fields) */}
      {ticket.fieldValues && ticket.fieldValues.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h2 className="text-sm font-semibold text-[#010E23] mb-3">Additional Information</h2>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2">
            {ticket.fieldValues.map(fv => (
              <div key={fv.id}>
                <p className="text-xs font-medium text-gray-400">{fv.field.fieldLabel}</p>
                <p className="text-sm text-[#010E23] mt-0.5">
                  {fv.field.fieldType === 'checkbox'
                    ? (fv.value === 'true' ? 'Yes' : 'No')
                    : fv.value || '—'}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── ASSIGN PANEL (IT team, once the ticket is Approved/Deferred/Assigned) ─── */}
      {canAssign && (
        <div className="bg-white rounded-xl border border-violet-200 shadow-sm p-5 space-y-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-orange-500" />
            <h2 className="text-sm font-semibold text-[#010E23]">Assign This Ticket</h2>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Assign to</label>
              <select
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-violet-600 bg-white"
                value={assignTarget}
                onChange={e => setAssignTarget(e.target.value)}
              >
                <option value="self">Assign to myself</option>
                {itStaff.filter(s => s.id !== user?.id).map(s => (
                  <option key={s.id} value={s.id}>{s.name}{s.isAvailable === false ? ' (Away)' : ''}</option>
                ))}
              </select>
            </div>
            {assignTarget !== 'self' && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Reason for assigning to another IT staff <span className="text-red-500">*</span>
                </label>
                <textarea
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-violet-600 bg-white resize-none"
                  rows={2}
                  value={assignReason}
                  onChange={e => setAssignReason(e.target.value)}
                  placeholder="Why are you assigning this to someone else?"
                />
              </div>
            )}
            {isITManager && !ticket.assignedToId && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  SLA for this ticket (hours) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number" min={1} max={720}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-violet-600 bg-white"
                  value={assignSla}
                  onChange={e => setAssignSla(e.target.value)}
                  placeholder="e.g. 24"
                />
                <p className="text-[11px] text-gray-400 mt-1">Pre-filled from priority — adjust if this ticket needs a different turnaround.</p>
              </div>
            )}
            {(() => {
              const targetResolvedId = assignTarget === 'self' ? user?.id : assignTarget;
              const alreadyAssignedToTarget = !!ticket.assignedToId && ticket.assignedToId === targetResolvedId;
              const slaMissing = isITManager && !ticket.assignedToId && !assignSla.trim();
              return (
                <Button
                  variant="primary"
                  size="sm"
                  disabled={assigning || !assignTarget || alreadyAssignedToTarget || slaMissing || (assignTarget !== 'self' && !assignReason.trim())}
                  onClick={doAssign}
                >
                  {assigning ? 'Assigning…' : alreadyAssignedToTarget ? 'Already Assigned' : 'Assign →'}
                </Button>
              );
            })()}
          </div>
        </div>
      )}

      {/* Confirm Resolved (for requester) */}
      {canConfirmResolved && (
        <div className="bg-white rounded-xl border border-teal-200 shadow-sm p-5 space-y-3">
          <h2 className="text-sm font-semibold text-teal-800">Confirm Resolution</h2>
          <p className="text-sm text-gray-600">Has your request been resolved to your satisfaction?</p>
          <div className="flex gap-2">
            <Button variant="success" size="sm" disabled={working} onClick={confirmResolved}>
              {working ? 'Confirming…' : 'Yes — Confirm Resolution'}
            </Button>
          </div>
        </div>
      )}

      {/* Requester already confirmed — waiting on the assignee's SR Closed to finalize */}
      {isRequester && status === 'Resolved' && ticket.requesterConfirmedAt && (
        <div className="bg-teal-50 rounded-xl border border-teal-100 p-5 space-y-1">
          <h2 className="text-sm font-semibold text-teal-800">Resolution Confirmed</h2>
          <p className="text-sm text-teal-700">
            You confirmed on {fmtDT(ticket.requesterConfirmedAt)}. The IT team will finalize closure shortly.
          </p>
        </div>
      )}

      {/* IT Manager triage on an Approved, unassigned ticket — before assigning */}
      {canTriageApproved && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-3">
          <h2 className="text-sm font-semibold text-[#010E23]">Triage</h2>
          <div className="flex gap-2 flex-wrap">
            <Button variant="ghost" size="sm" onClick={() => { setClarifyModal(true); setClarifyNote(''); }}>
              Return for Clarification
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { setDeferModal(true); setDeferReason(''); setDeferUntil(''); }}>
              Defer
            </Button>
            <Button variant="danger" size="sm" onClick={() => { setCannotModal(true); setCannotCategory(''); setCannotExplain(''); }}>
              Cannot Implement
            </Button>
          </div>
        </div>
      )}

      {/* Actions */}
      {(canApprove || canTransfer || canRedirectLM || canStartWork || canResolve || canCloseSR) && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-3">
          <h2 className="text-sm font-semibold text-[#010E23]">Actions</h2>
          {action && (
            <>
              <KbSuggestions query={ticket.items?.[0]?.category || ticket.purpose || ''} />
              <div className="flex justify-end">
                <CannedResponsePicker onInsert={body => setComment(c => c ? `${c}\n${body}` : body)} />
              </div>
            </>
          )}
          {action === 'transfer' && topMgmtApprovers.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Send to — select one or more (all checked approvers must sign off, in order)
              </label>
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                {topMgmtApprovers.map(s => {
                  const disabled = !s.approverUserId || !s.approverActive;
                  const checked  = selectedStepOrders.includes(s.stepOrder);
                  return (
                    <label key={s.stepId}
                      className={`flex items-center gap-2 px-3 py-2 text-sm ${disabled ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 cursor-pointer hover:bg-gray-50'}`}>
                      <input type="checkbox" className="rounded text-violet-600" disabled={disabled}
                        checked={checked}
                        onChange={e => setSelectedStepOrders(prev =>
                          e.target.checked ? [...prev, s.stepOrder] : prev.filter(so => so !== s.stepOrder))}
                      />
                      {ordinal(s.stepOrder)} — {s.approverName ?? 'Unassigned'}{!s.approverActive ? ' (inactive)' : ''}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          {action === 'redirectLm' && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Redirect to</label>
              <select
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-violet-600 bg-white"
                value={redirectTarget}
                onChange={e => setRedirectTarget(e.target.value)}
              >
                <option value="">Select a Line Manager…</option>
                {lineManagers.filter(lm => lm.id !== ticket.lineManagerId).map(lm => (
                  <option key={lm.id} value={lm.id}>{lm.name}{lm.department ? ` — ${lm.department}` : ''}</option>
                ))}
              </select>
            </div>
          )}
          {action && (
            <textarea
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-violet-600 resize-none"
              rows={3}
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder={
                action === 'reject'     ? 'Rejection reason (required)…' :
                action === 'transfer'   ? 'Note for Top Management (optional)…' :
                action === 'resolve'    ? 'Resolution note (optional)…' :
                action === 'redirectLm' ? 'Reason for redirecting (optional)…' :
                'Comment (optional)…'
              }
            />
          )}
          <div className="flex gap-2 flex-wrap">
            {canApprove && (
              <>
                <Button variant="success" size="sm" disabled={working}
                  onClick={() => action === 'approve' ? approve() : setAction('approve')}>
                  {action === 'approve' ? (working ? 'Approving…' : 'Confirm Approve') : 'Approve'}
                </Button>
                <Button variant="danger" size="sm" disabled={working}
                  onClick={() => action === 'reject' ? reject() : setAction('reject')}>
                  {action === 'reject' ? (working ? 'Rejecting…' : 'Confirm Reject') : 'Reject'}
                </Button>
              </>
            )}
            {canApproveITFirst && (
              <Button variant="ghost" size="sm" disabled={working}
                onClick={() => { setClarifyModal(true); setClarifyNote(''); }}>
                Request Clarification
              </Button>
            )}
            {canTransfer && (
              <Button variant="secondary" size="sm" disabled={working}
                onClick={() => action === 'transfer' ? transfer() : setAction('transfer')}>
                {action === 'transfer' ? (working ? 'Transferring…' : 'Confirm Transfer') : '⬆ Transfer to Top Mgmt'}
              </Button>
            )}
            {canRedirectLM && (
              <Button variant="ghost" size="sm" disabled={working}
                onClick={() => action === 'redirectLm' ? redirectLm() : setAction('redirectLm')}>
                {action === 'redirectLm' ? (working ? 'Redirecting…' : 'Confirm Redirect') : '↷ Redirect LM Approval'}
              </Button>
            )}
            {canStartWork && (
              <Button variant="primary" size="sm" disabled={working} onClick={startWork}>
                {working ? 'Starting…' : '▶ Start Work'}
              </Button>
            )}
            {canResolve && (
              <Button variant="success" size="sm" disabled={working}
                onClick={() => action === 'resolve' ? resolve() : setAction('resolve')}>
                {action === 'resolve' ? (working ? 'Marking Resolved…' : 'Confirm Resolved') : 'Mark Resolved'}
              </Button>
            )}
            {canCloseSR && (
              srCloseEligible ? (
                <Button variant="primary" size="sm" onClick={() => setShowCloseModal(true)}>
                  SR Closed — Finalize Closure
                </Button>
              ) : (
                <span
                  title="Waiting so the requester has a chance to confirm — SR Closed unblocks immediately once they do"
                  className="inline-flex items-center text-xs text-gray-400 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 cursor-help"
                >
                  SR Closed available from {srCloseAvailableAt ? fmtDT(srCloseAvailableAt) : '—'} (or sooner if the requester confirms)
                </span>
              )
            )}
            {action && (
              <Button variant="ghost" size="sm"
                onClick={() => { setAction(null); setComment(''); }}>
                Cancel
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <h2 className="text-sm font-semibold text-[#010E23] mb-3">Timeline</h2>
        <div className="space-y-3">
          {(ticket.timeline || []).length === 0 ? (
            <p className="text-xs text-gray-400">No timeline entries</p>
          ) : (ticket.timeline || []).map((e: TimelineEntry, i: number) => (
            <div key={i} className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className="w-2 h-2 rounded-full bg-violet-600 mt-1 shrink-0" />
                {i < (ticket.timeline?.length ?? 0) - 1 && (
                  <div className="flex-1 w-px bg-gray-200 my-1" />
                )}
              </div>
              <div className="pb-3 min-w-0">
                <p className="text-xs font-medium text-[#010E23]">{e.action}</p>
                {(e.note || e.comment) && (
                  <p className="text-xs text-gray-500 mt-0.5">{e.note || e.comment}</p>
                )}
                <p className="text-[10px] text-gray-300 mt-0.5">
                  {e.user?.name ?? e.actorName ?? '—'} · {fmtDT(e.performedAt ?? e.createdAt)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── SR Closed Modal ──────────────────────────────────────── */}
      {showCloseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
            <h2 className="text-base font-bold text-[#010E23]">Close SR</h2>
            <p className="text-xs text-gray-500">
              Ref: <span className="font-mono text-violet-700">{ticket.id}</span>
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Resolution Notes <span className="text-red-500">*</span>
                </label>
                <textarea
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-violet-600 resize-none"
                  rows={4}
                  placeholder="Describe what was done to resolve this request…"
                  value={resolutionNotes}
                  onChange={e => setResolutionNotes(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Time Spent (hours)</label>
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-violet-600"
                    placeholder="e.g. 2.5"
                    value={timeSpent}
                    onChange={e => setTimeSpent(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Closure Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-violet-600 bg-white"
                    value={closureCategory}
                    onChange={e => setClosureCategory(e.target.value as any)}
                  >
                    <option value="">Select…</option>
                    {CLOSURE_CATEGORIES.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="primary"
                size="sm"
                disabled={closingTicket || !resolutionNotes.trim() || !closureCategory}
                onClick={closeSR}
              >
                {closingTicket ? 'Closing…' : 'Submit & Close SR'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setShowCloseModal(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {clarifyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
            <h2 className="text-base font-bold text-[#010E23]">Return for Clarification</h2>
            <p className="text-sm text-gray-500">This ticket will be returned to the requester.</p>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                What information is needed? <span className="text-red-500">*</span>
              </label>
              <textarea
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-violet-600 resize-none"
                rows={4}
                placeholder="Describe what additional information is required…"
                value={clarifyNote}
                onChange={e => setClarifyNote(e.target.value)}
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="primary" size="sm" disabled={clarifying || !clarifyNote.trim()} onClick={doClarify}>
                {clarifying ? 'Sending…' : 'Return for Clarification'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setClarifyModal(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {deferModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
            <h2 className="text-base font-bold text-[#010E23]">Defer Ticket</h2>
            <p className="text-sm text-gray-500">This ticket will be deferred.</p>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Reason for deferral <span className="text-red-500">*</span>
              </label>
              <textarea
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-violet-600 resize-none"
                rows={3}
                placeholder="Why is this being deferred?"
                value={deferReason}
                onChange={e => setDeferReason(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Target review date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-violet-600"
                value={deferUntil}
                min={new Date().toISOString().slice(0, 10)}
                onChange={e => setDeferUntil(e.target.value)}
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="primary" size="sm" disabled={deferring || !deferReason.trim() || !deferUntil} onClick={doDefer}>
                {deferring ? 'Deferring…' : 'Confirm Deferral'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setDeferModal(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {cannotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
            <h2 className="text-base font-bold text-[#010E23]">Cannot Implement</h2>
            <p className="text-sm text-gray-500">This ticket will be closed.</p>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Reason category <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-violet-600 bg-white"
                value={cannotCategory}
                onChange={e => setCannotCategory(e.target.value)}
              >
                <option value="">Select a reason…</option>
                {CANNOT_IMPLEMENT_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Detailed explanation <span className="text-red-500">*</span>
              </label>
              <textarea
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-violet-600 resize-none"
                rows={4}
                placeholder="Provide a detailed explanation…"
                value={cannotExplain}
                onChange={e => setCannotExplain(e.target.value)}
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="danger" size="sm" disabled={cannotWorking || !cannotCategory || !cannotExplain.trim()} onClick={doCannot}>
                {cannotWorking ? 'Closing…' : 'Close Ticket'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setCannotModal(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-sm font-medium text-[#010E23] mt-0.5">{value}</p>
    </div>
  );
}
